// Server-side aggregation hook for the Explorer chart. The chat-embedded
// chart consumes the planner's AggregationResult directly (no extra query
// needed); this hook is for "Mónica picks Group by Country" in the rail —
// it aggregates the *full* filtered set rather than the visible page.
import { useQuery } from "@tanstack/react-query";
import { aggregateContacts } from "../lib/api";
import type {
  AggregationResult,
  AggregationSort,
  ChartType,
} from "../types/chart";
import type { FilterGroup } from "../types/inpact";

export interface UseAggregationParams {
  filterGroup: FilterGroup;
  groupBy: string;
  chartType: ChartType;
  topN?: number | null;
  sort?: AggregationSort;
  enabled?: boolean;
}

export interface UseAggregationResult {
  data: AggregationResult | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  resultCode: "ok" | "no_matches" | null;
}

export function useAggregation(params: UseAggregationParams): UseAggregationResult {
  const enabled = params.enabled !== false && Boolean(params.groupBy);
  const q = useQuery({
    queryKey: ["aggregate", params.filterGroup, params.groupBy, params.chartType, params.topN, params.sort],
    queryFn: () => aggregateContacts({
      filter_group: params.filterGroup,
      group_by: params.groupBy,
      chart_type: params.chartType,
      top_n: params.topN ?? null,
      sort: params.sort ?? "desc",
    }),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
  const bound = Boolean(
    q.data
    && q.data.chart_spec.group_by === params.groupBy
    && JSON.stringify(q.data.applied_filter_group) === JSON.stringify(params.filterGroup),
  );
  const bindingError = q.data && !bound
    ? new Error("Aggregate response did not match the current query")
    : null;
  const usable = bound && !q.error ? q.data ?? null : null;
  return {
    data: usable,
    isLoading: q.isLoading,
    isFetching: q.isFetching,
    error: bindingError ?? q.error ?? null,
    resultCode: usable?.result_code ?? null,
  };
}
